import { useGetAlphaBscWalletQuery } from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

export default function AlphaBscWalletPage() {
  const w = useGetAlphaBscWalletQuery(undefined, { pollingInterval: 15000 });
  const data: any = w.data || {};
  const configured = data.configured === true;

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">BSC wallet</h1>
        <p className="text-sm text-muted">
          The bot's externally-owned account on BNB Smart Chain.
          USDT (BEP-20) funds trades, BNB pays gas. All on-chain transactions
          are signed by this wallet's private key (stored in <code className="font-mono">.env</code>,
          file perms 600).
        </p>
      </header>

      {w.isLoading && <Skeleton className="h-32 w-full" />}

      {!w.isLoading && !configured && (
        <Card>
          <CardHeader><CardTitle className="text-yellow-500">Wallet not configured</CardTitle></CardHeader>
          <CardContent>
            <div className="text-sm space-y-2">
              <p>SSH into the server and generate a wallet:</p>
              <pre className="text-xs bg-muted/20 p-3 rounded font-mono whitespace-pre-wrap">
{`cd /home/ec2-user/trading-ai-multi
/home/ec2-user/trading-ai/.venv/bin/python -m bot.web3_layer.bsc_wallet generate

# Then copy BSC_WALLET_ADDRESS + BSC_WALLET_PRIVATE_KEY into .env
sudo systemctl restart trading-ai-multi.service`}
              </pre>
              <p className="text-muted">
                After restart, fund the address from Binance via BSC (BEP-20)
                network: $50+ USDT + ~0.02 BNB for gas.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {!w.isLoading && configured && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Account</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-muted">Address</div>
                  <a href={data.bscscan_url} target="_blank" rel="noopener noreferrer"
                     className="font-mono hover:underline break-all">
                    {data.address}
                  </a>
                  <div className="text-xs text-muted mt-1">
                    Click to open BscScan ↗
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted">RPC node</div>
                  <div>
                    {data.rpc_connected ? (
                      <Badge tone="good">connected · chain {data.chain_id} · block {Number(data.latest_block ?? 0).toLocaleString()}</Badge>
                    ) : (
                      <Badge tone="bad">offline</Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted mt-1">
                    Default: bsc-dataseed.binance.org (override via BSC_RPC_URL in .env)
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Balances</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <BalCard
                  label="BNB (gas)"
                  amount={Number(data.bnb_balance ?? 0).toFixed(8)}
                  warn={data.bnb_balance < 0.005 ? "Low — fund more BNB for gas" : ""}
                  tone={data.bnb_balance < 0.005 ? "bad" : "good"}
                />
                <BalCard
                  label="USDT (BEP-20)"
                  amount={`$${Number(data.usdt_balance?.formatted ?? 0).toFixed(6)}`}
                  warn={data.usdt_balance?.formatted < 10 ? "Below $10 — limits trade size" : ""}
                  tone={data.usdt_balance?.formatted >= 25 ? "good" : "warn"}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Funding flow</CardTitle></CardHeader>
            <CardContent>
              <div className="text-sm space-y-2">
                <p>To add capital:</p>
                <ol className="list-decimal list-inside text-xs space-y-1 ml-2">
                  <li>Binance → Wallet → Withdraw</li>
                  <li>Asset: <strong>USDT</strong> (for capital) or <strong>BNB</strong> (for gas)</li>
                  <li>Network: <strong>BNB Smart Chain (BEP-20)</strong> — DO NOT pick ERC-20</li>
                  <li>Address: <code className="text-[10px] font-mono break-all">{data.address}</code></li>
                  <li>Amount: any (min ~$10)</li>
                </ol>
                <p className="text-xs text-muted">
                  Withdrawal fee ~$0.30 USDT or trivial BNB. Arrives in ~30 seconds.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Security</CardTitle></CardHeader>
            <CardContent>
              <ul className="text-sm space-y-2 list-disc list-inside">
                <li>Private key stored in <code className="font-mono">.env</code> with file perms 600 (root + ec2-user only).</li>
                <li>Hard cap per trade: $100. Hard cap per day: $500. Cannot be raised via settings.</li>
                <li>Slippage protection: 2% default (refuses fills outside tolerance).</li>
                <li>Backup the privkey somewhere safe (1Password / hardware wallet seed) — losing the server without backup loses the funds.</li>
              </ul>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function BalCard({ label, amount, warn, tone }: {
  label: string; amount: string; warn?: string; tone: "good" | "warn" | "bad";
}) {
  const colors = { good: "text-green-500", warn: "text-yellow-500", bad: "text-red-500" };
  return (
    <div className="rounded border p-4 bg-muted/5">
      <div className="text-xs text-muted">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums ${colors[tone]}`}>{amount}</div>
      {warn && <div className="text-xs text-yellow-500 mt-1">⚠ {warn}</div>}
    </div>
  );
}
